import React, { useCallback, useState, useEffect, useContext } from 'react';
import classNames from 'classnames';
import get from 'lodash.get';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { createSelector } from 'reselect';

import { TreeNav } from 'ui-components/components/tree-navigation';
import { noop } from 'ui-components/default-props/noop';

import { OrdersLeftNavContext, useSelectedOrdersViewId } from './orders-left-nav-context';
import style from './orders-left-nav.module.less';
import { buildTagProps } from './route-helper';
import AdvancedSearchToggler from '../../../advanced-search/advanced-search-toggler';
import { useIsOnOrdersAdvancedSearchView } from '../../../advanced-search/hooks';
import AdvancedSearch from '../../../advanced-search/orders';
import { shouldOpenAdvancedSearchFlag } from '../../../advanced-search/orders/constants';
import { SearchForm } from '../../../components/orders-grid/search-form';
import { OrdersPageAddBatchMenuContainer } from '../../../containers/batches/add-batch-menu-containers/orders-page-add-batch-menu-container';
import { useQuery } from '../../../hooks/routing/use-query';
import { useQuickSearchTerm } from '../../../hooks/routing/use-quick-search-term';
import {
  copySearchTermsToPath,
  pushToHistoryWithSearchTerm,
  QUICK_SEARCH_ROUTE_ROOT,
} from '../../../services/quicksearch-location-service';
import { hasQuickSearchTerm } from '../../../services/quicksearch-service';
import { DEFAULT_ORDERS_PAGE_URL } from '../../../services/user-permission-service';
import Global from '../../../store/global-selectors';
import { changeViewSearch, clearSearchInput, handleSearchTermChange } from '../../../store/orders/nav/orders.nav.actions';
import {
  ADVANCED_SEARCH_ID,
  AWAITING_SHIPMENT_ID,
  ORDER_ALERTS_VIEW,
  ORDER_ALERTS_SHIP_TOGETHER_VIEW,
} from '../orders-page-view-constants';

const defaultFormDataSelector = createSelector(
  (searchData) => searchData,
  (searchData) => ({
    searchTerm: get(searchData, 'searchTerm', ''),
  }),
);

const routePrefix = '/orders/';

export const OrdersLeftNav = () => {
  const { ordersGridViews } = useContext(OrdersLeftNavContext);
  const searchData = useSelector(Global.selectOrdersNavSearchData);
  const defaultFormData = defaultFormDataSelector(searchData);
  const history = useHistory();
  const dispatch = useDispatch();
  const quickSearchTerm = useQuickSearchTerm();
  const query = useQuery();
  const openFlag = !!query.get(shouldOpenAdvancedSearchFlag);
  const quickSearchPerformed = hasQuickSearchTerm(quickSearchTerm);
  const isOnAdvancedSearchPage = useIsOnOrdersAdvancedSearchView();
  const [advancedSearchFormIsOpen, setAdvancedSearchFormOpen] = useState(isOnAdvancedSearchPage);
  const currentViewId = useSelectedOrdersViewId();
  const [previousViewId, setPreviousViewId] = useState(currentViewId);

  // Instead of relying on this side effect to clear search state we should instead move it out of redux and into the component tree in such a way that the state
  //  is only available to the components that need it and unmounted when navigating away from orders.
  //  https://auctane.atlassian.net/browse/SPD-13853
  useEffect(() => {
    return () => dispatch(clearSearchInput());
  }, [dispatch]);

  useEffect(() => {
    setAdvancedSearchFormOpen(isOnAdvancedSearchPage || openFlag);
  }, [isOnAdvancedSearchPage, setAdvancedSearchFormOpen, openFlag]);

  const onSearchExecuted = useCallback(() => {
    const { searchTerm } = defaultFormData;
    if (searchTerm && searchTerm.length) {
      pushToHistoryWithSearchTerm(history, searchTerm, QUICK_SEARCH_ROUTE_ROOT);
    }
    dispatch(changeViewSearch(defaultFormData, true));
  }, [dispatch, history, defaultFormData]);

  const generateTargetPath = useCallback(
    (e, historyLocation, fallbackPathname) => {
      const targetUrl = e.target.href ? new URL(e.target.href) : { pathname: fallbackPathname };
      const isTargetAlerts =
        targetUrl.pathname.includes(ORDER_ALERTS_VIEW.routePath) || targetUrl.pathname.includes(ORDER_ALERTS_SHIP_TOGETHER_VIEW.routePath);
      if (quickSearchPerformed) {
        if (isTargetAlerts) {
          return targetUrl.pathname;
        }
        return copySearchTermsToPath(historyLocation, targetUrl.pathname);
      }
      return targetUrl.pathname;
    },
    [quickSearchPerformed],
  );

  const onExpandAndSelect = useCallback(
    (view, e) => {
      e.preventDefault();
      const { to: fallbackPathname } = buildTagProps(view);
      history.push(generateTargetPath(e, history.location, fallbackPathname));
    },
    [generateTargetPath, history],
  );

  const onToggleAdvancedSearch = useCallback(() => {
    const isAboutToLeaveAdvancedSearch = advancedSearchFormIsOpen;
    if (isAboutToLeaveAdvancedSearch) {
      const shouldUseDefaultView = !previousViewId || previousViewId === ADVANCED_SEARCH_ID || previousViewId === AWAITING_SHIPMENT_ID;
      if (shouldUseDefaultView) {
        history.push(`${routePrefix}${AWAITING_SHIPMENT_ID}`);
      } else {
        history.goBack();
      }
    } else {
      setPreviousViewId(currentViewId);
    }
    setAdvancedSearchFormOpen(!advancedSearchFormIsOpen);
  }, [advancedSearchFormIsOpen, history, setAdvancedSearchFormOpen, currentViewId, previousViewId]);

  const onClearSearchInput = useCallback(() => {
    dispatch(clearSearchInput());
    history.push(DEFAULT_ORDERS_PAGE_URL);
  }, [dispatch, history]);

  const onSearchTermChange = useCallback((term) => dispatch(handleSearchTermChange(term)), [dispatch]);
  const isSearchAreaHighlighted = quickSearchPerformed && !advancedSearchFormIsOpen;
  const showSearchAreaBorder = !advancedSearchFormIsOpen;

  return (
    <div className={style['tree-nav-container']}>
      <div
        className={classNames({
          [style['search-area']]: advancedSearchFormIsOpen,
          [style['search-area-highlighted']]: isSearchAreaHighlighted,
        })}
      >
        <div
          className={classNames({
            [style['search-area-buttons']]: showSearchAreaBorder,
          })}
        >
          {!advancedSearchFormIsOpen && (
            <SearchForm
              clearSearchInput={onClearSearchInput}
              defaultFormData={defaultFormData}
              getSearchTermCount={noop}
              handleSearchInputChange={onSearchTermChange}
              isHighlighted={quickSearchPerformed}
              onSearch={onSearchExecuted}
            />
          )}
          <AdvancedSearchToggler isInAdvancedSearch={advancedSearchFormIsOpen} onToggleAdvancedSearch={onToggleAdvancedSearch} />
        </div>
        {advancedSearchFormIsOpen ? <AdvancedSearch /> : null}
      </div>
      {!advancedSearchFormIsOpen && (
        <div className={style['tree-and-batches']}>
          <TreeNav
            buildTagProps={buildTagProps}
            isHighlighted={quickSearchPerformed}
            items={ordersGridViews}
            onExpandClick={noop}
            onItemSelected={onExpandAndSelect}
          />
          <OrdersPageAddBatchMenuContainer />
        </div>
      )}
    </div>
  );
};
